import { AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import type { LicenseCriteria } from '../../types'
import { formatDate } from './helpers'

const ID_PREFIX_LENGTH = 8

interface CreateLicenseCriteriaStepProps {
  linkedCriteria: LicenseCriteria | null
  linkableCriteria: LicenseCriteria[]
  selectedCriteriaToLink: string
  setSelectedCriteriaToLink: (id: string) => void
  linking: boolean
  onLinkCriteria: () => void
}

function LinkedCriteriaCard({ criteria }: { criteria: LicenseCriteria }): React.ReactElement {
  const validUntilDisplay = criteria.validUntil ? formatDate(criteria.validUntil) : 'Perpetual'
  const featuresDisplay =
    criteria.featuresGranted.length > 0 ? criteria.featuresGranted.join(', ') : 'None'
  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm dark:border-green-800 dark:bg-green-900/20">
      <div className="mb-2 flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="font-medium text-green-800 dark:text-green-300">
          Criteria linked to this invoice
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-stone-700 dark:text-stone-300">
        <div>
          <span className="text-stone-500 dark:text-stone-400">Reference:</span>{' '}
          {criteria.externalReference ?? 'N/A'}
        </div>
        <div>
          <span className="text-stone-500 dark:text-stone-400">Tier:</span>{' '}
          <span className="capitalize">{criteria.accessTier}</span>
        </div>
        <div>
          <span className="text-stone-500 dark:text-stone-400">Seats:</span> {criteria.seatsGranted}
        </div>
        <div>
          <span className="text-stone-500 dark:text-stone-400">Valid From:</span>{' '}
          {formatDate(criteria.validFrom)}
        </div>
        <div>
          <span className="text-stone-500 dark:text-stone-400">Valid Until:</span>{' '}
          {validUntilDisplay}
        </div>
        <div>
          <span className="text-stone-500 dark:text-stone-400">Features:</span> {featuresDisplay}
        </div>
      </div>
    </div>
  )
}

interface LinkableCriteriaPickerProps {
  linkableCriteria: LicenseCriteria[]
  selectedCriteriaToLink: string
  setSelectedCriteriaToLink: (id: string) => void
  linking: boolean
  onLinkCriteria: () => void
}

function LinkableCriteriaPicker(props: LinkableCriteriaPickerProps): React.ReactElement {
  const {
    linkableCriteria,
    selectedCriteriaToLink,
    setSelectedCriteriaToLink,
    linking,
    onLinkCriteria,
  } = props
  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-amber-700 dark:text-amber-300">
          No criteria linked to this invoice. Select a verified criteria to link:
        </span>
      </div>
      <div className="flex gap-2">
        <select
          value={selectedCriteriaToLink}
          onChange={(event) => {
            setSelectedCriteriaToLink(event.target.value)
          }}
          className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-[#4e5e82] focus:ring-2 focus:ring-[#4e5e82] dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100"
        >
          <option value="">Select criteria to link...</option>
          {linkableCriteria.map((criterion) => (
            <option key={criterion.id} value={criterion.id}>
              {criterion.externalReference ?? criterion.id.slice(0, ID_PREFIX_LENGTH)} -{' '}
              {criterion.accessTier} ({criterion.seatsGranted} seats)
            </option>
          ))}
        </select>
        <button
          onClick={onLinkCriteria}
          disabled={!selectedCriteriaToLink || linking}
          className="rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {linking ? 'Linking...' : 'Link'}
        </button>
      </div>
    </div>
  )
}

function NoLinkableCriteriaMessage(): React.ReactElement {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-amber-700 dark:text-amber-300">
          No verified criteria available to link. Create and verify criteria first.
        </span>
      </div>
    </div>
  )
}

function selectCriteriaBody(props: CreateLicenseCriteriaStepProps): React.ReactElement {
  const {
    linkedCriteria,
    linkableCriteria,
    selectedCriteriaToLink,
    setSelectedCriteriaToLink,
    linking,
    onLinkCriteria,
  } = props
  if (linkedCriteria) return <LinkedCriteriaCard criteria={linkedCriteria} />
  if (linkableCriteria.length > 0) {
    return (
      <LinkableCriteriaPicker
        linkableCriteria={linkableCriteria}
        selectedCriteriaToLink={selectedCriteriaToLink}
        setSelectedCriteriaToLink={setSelectedCriteriaToLink}
        linking={linking}
        onLinkCriteria={onLinkCriteria}
      />
    )
  }
  return <NoLinkableCriteriaMessage />
}

export function CreateLicenseCriteriaStep(
  props: CreateLicenseCriteriaStepProps
): React.ReactElement {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
        3. Linked Criteria (License Gate)
      </label>
      {selectCriteriaBody(props)}
    </div>
  )
}
